/**
 * drawingReplay.js
 *
 * Pure module for replaying drawing stroke data onto a canvas.
 * No React dependency — can be tested standalone in any HTML page or Node canvas.
 *
 * Drawing data model:
 * {
 *   canvas: { width: 1600, height: 900, background: '#ffffff' },
 *   strokes: [
 *     {
 *       id: string,
 *       type: 'stroke' | 'clear' | 'fill',
 *       tool: 'brush' | 'eraser',   // only for type='stroke'
 *       color: string,               // only for type='stroke' | 'fill'
 *       size: number,                // only for type='stroke'
 *       points: [{ x, y, t }],       // only for type='stroke', t = ms since start
 *       x: number,                   // only for type='fill'
 *       y: number,                   // only for type='fill'
 *       startTime: number,           // ms since drawing began
 *       endTime: number,             // ms since drawing began
 *     }
 *   ],
 *   totalDuration: number  // ms
 * }
 */

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
    a: 255,
  };
}

/**
 * Flood-fill the canvas at (startX, startY) with fillColor.
 * Uses a stack-based 4-direction fill with tolerance for anti-aliased edges.
 */
export function floodFill(ctx, startX, startY, fillColor, tolerance = 40) {
  const canvas = ctx.canvas;
  const { width, height } = canvas;
  const px = Math.round(startX);
  const py = Math.round(startY);
  if (px < 0 || px >= width || py < 0 || py >= height) return;

  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const fill = hexToRgb(fillColor);

  const si = (py * width + px) * 4;
  const tr = data[si], tg = data[si + 1], tb = data[si + 2], ta = data[si + 3];

  // Already this color — nothing to do
  if (
    Math.abs(tr - fill.r) <= tolerance &&
    Math.abs(tg - fill.g) <= tolerance &&
    Math.abs(tb - fill.b) <= tolerance &&
    Math.abs(ta - fill.a) <= tolerance
  ) return;

  function matches(i) {
    return (
      Math.abs(data[i]     - tr) <= tolerance &&
      Math.abs(data[i + 1] - tg) <= tolerance &&
      Math.abs(data[i + 2] - tb) <= tolerance &&
      Math.abs(data[i + 3] - ta) <= tolerance
    );
  }

  const visited = new Uint8Array(width * height);
  // Stack stores x,y pairs as flat values for performance
  const stack = [px, py];

  while (stack.length >= 2) {
    const y = stack.pop();
    const x = stack.pop();
    if (x < 0 || x >= width || y < 0 || y >= height) continue;
    const flat = y * width + x;
    if (visited[flat]) continue;
    visited[flat] = 1;
    const pi = flat * 4;
    if (!matches(pi)) continue;
    data[pi]     = fill.r;
    data[pi + 1] = fill.g;
    data[pi + 2] = fill.b;
    data[pi + 3] = fill.a;
    stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
  }

  ctx.putImageData(imageData, 0, 0);
}

/**
 * Render all strokes up to `upToTime` (ms) onto the given canvas 2D context.
 * Draws from scratch each call (clears first), so it's idempotent.
 */
export function renderStrokes(ctx, strokes, upToTime = Infinity, background = '#ffffff') {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;

  // Clear to background
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, w, h);

  if (!strokes || !Array.isArray(strokes)) return;

  for (let i = 0; i < strokes.length; i++) {
    const stroke = strokes[i];
    if (!stroke) continue;
    if (stroke.startTime == null) continue;
    if (stroke.startTime > upToTime) break;

    if (stroke.type === 'clear') {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, w, h);
      continue;
    }

    if (stroke.type === 'fill') {
      floodFill(ctx, stroke.x, stroke.y, stroke.color || '#000000');
      continue;
    }

    if (stroke.type !== 'stroke') continue;
    if (!stroke.points || stroke.points.length === 0) continue;

    const isEraser = stroke.tool === 'eraser';
    const drawColor = isEraser ? background : (stroke.color || '#000000');
    const drawSize = isEraser ? (stroke.size || 6) * 2 : (stroke.size || 6);

    // Filter points up to the target time
    const points = upToTime === Infinity
      ? stroke.points
      : stroke.points.filter(p => p.t <= upToTime);

    if (points.length === 0) continue;

    // Draw initial dot
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, drawSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = drawColor;
    ctx.fill();

    // Draw line segments
    if (points.length > 1) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let j = 1; j < points.length; j++) {
        ctx.lineTo(points[j].x, points[j].y);
      }
      ctx.strokeStyle = drawColor;
      ctx.lineWidth = drawSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  }
}

/**
 * Trim dead time from drawing data:
 *  - Remove silence before the first stroke (shift all timestamps so first stroke starts at 0)
 *  - Remove silence after the last stroke (set totalDuration to last stroke endTime)
 *  - Collapse large gaps between strokes (cap inter-stroke pauses to maxGap ms)
 *
 * Returns a new drawingData object (does not mutate the original).
 */
export function trimDrawingData(drawingData, { maxGap = 500 } = {}) {
  if (!drawingData?.strokes || drawingData.strokes.length === 0) {
    return drawingData;
  }

  const strokes = drawingData.strokes;

  // 1. Find the earliest startTime across all strokes
  const firstStart = strokes[0].startTime ?? 0;

  // 2. Build trimmed strokes: shift everything so first stroke starts at 0,
  //    and collapse large inter-stroke gaps.
  let cumulativeShift = firstStart; // total ms removed so far
  const trimmed = strokes.map((stroke, i) => {
    // For strokes after the first, check the gap from the previous stroke's end
    if (i > 0) {
      const prevEnd = strokes[i - 1].endTime ?? strokes[i - 1].startTime ?? 0;
      const gap = (stroke.startTime ?? 0) - prevEnd;
      if (gap > maxGap) {
        cumulativeShift += gap - maxGap; // remove the excess beyond maxGap
      }
    }

    const shift = cumulativeShift;

    if (stroke.type === 'clear' || stroke.type === 'fill') {
      return {
        ...stroke,
        startTime: (stroke.startTime ?? 0) - shift,
        endTime: (stroke.endTime ?? 0) - shift,
      };
    }

    // Shift all point timestamps too
    const shiftedPoints = stroke.points
      ? stroke.points.map(p => ({ ...p, t: p.t - shift }))
      : [];

    return {
      ...stroke,
      startTime: (stroke.startTime ?? 0) - shift,
      endTime: (stroke.endTime ?? 0) - shift,
      points: shiftedPoints,
    };
  });

  // 3. totalDuration = end of last stroke
  const lastStroke = trimmed[trimmed.length - 1];
  const newDuration = lastStroke.endTime ?? lastStroke.startTime ?? 0;

  return {
    ...drawingData,
    strokes: trimmed,
    totalDuration: newDuration,
  };
}

/**
 * Creates an animated playback controller.
 *
 * Usage:
 *   const player = createPlayer(canvas, drawingData, { speed: 3, onFinished: () => {} });
 *   player.play();
 *   player.stop();
 *   player.seekTo(0.5); // 50%
 *
 * Returns { play, stop, seekTo, destroy }
 */
export function createPlayer(canvas, drawingData, options = {}) {
  const { speed = 1, onProgress, onFinished } = options;
  const ctx = canvas.getContext('2d');

  // Trim dead time before/after drawing and collapse long pauses
  const trimmed = trimDrawingData(drawingData);
  const strokes = trimmed?.strokes || [];
  const totalDuration = trimmed?.totalDuration || 0;
  const background = trimmed?.canvas?.background || '#ffffff';

  // Set canvas size
  canvas.width = drawingData?.canvas?.width || 800;
  canvas.height = drawingData?.canvas?.height || 600;

  console.log('[createPlayer] Initialized (trimmed):', {
    canvasSize: `${canvas.width}x${canvas.height}`,
    strokeCount: strokes.length,
    originalDuration: drawingData?.totalDuration,
    trimmedDuration: totalDuration,
    background,
    speed,
    hasCtx: !!ctx,
  });

  let animFrameId = null;
  let startWallTime = null;
  let playing = false;
  let destroyed = false;

  // Initial blank
  renderStrokes(ctx, [], 0, background);

  let tickCount = 0;

  function tick() {
    if (!playing || destroyed) return;
    if (startWallTime === null) startWallTime = performance.now();

    const wallElapsed = performance.now() - startWallTime;
    const drawingTime = wallElapsed * speed;

    if (tickCount === 0) {
      console.log('[createPlayer tick] First frame:', {
        drawingTime,
        totalDuration,
        strokeCount: strokes.length,
        canvasActualSize: `${ctx.canvas.width}x${ctx.canvas.height}`,
      });
    }
    tickCount++;

    renderStrokes(ctx, strokes, drawingTime, background);

    const pct = totalDuration > 0 ? Math.min(drawingTime / totalDuration, 1) : 1;
    if (onProgress) onProgress(pct);

    if (drawingTime >= totalDuration || totalDuration === 0) {
      // Final render at infinity to ensure everything is drawn
      renderStrokes(ctx, strokes, Infinity, background);
      playing = false;
      if (onProgress) onProgress(1);
      if (onFinished) onFinished();
      return;
    }

    animFrameId = requestAnimationFrame(tick);
  }

  function play() {
    if (destroyed || playing) return;
    playing = true;
    startWallTime = null;
    animFrameId = requestAnimationFrame(tick);
  }

  function stop() {
    playing = false;
    if (animFrameId) {
      cancelAnimationFrame(animFrameId);
      animFrameId = null;
    }
  }

  function seekTo(fraction) {
    const t = fraction * totalDuration;
    renderStrokes(ctx, strokes, t, background);
    if (onProgress) onProgress(fraction);
  }

  function destroy() {
    stop();
    destroyed = true;
  }

  return { play, stop, seekTo, destroy };
}
